import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, ChangeDetectionStrategy } from '@angular/core';
import {
  IonButton,
  IonCol,
  IonGrid,
  IonIcon,
  IonItemDivider,
  IonItemGroup,
  IonList,
  IonRow,
  ModalController,
} from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SkeletonCardGenericComponent, SpinnerLoadingComponent } from '@okcargo-apps/layout-ionic';
import { MessagesUiService, MessageUiIonicComponent } from '@okcargo-apps/messages-ui';
import { AccountService } from '../../account';
import { IFreightAvailableDetailedVM } from '../freight-available-store.model';
import { FastpayCardCtaComponent } from '../../fast-payment/fastpay-card-cta/fastpay-card-cta.component';
import { HelpTrafficModalComponent } from '../../help-traffic-modal/help-traffic-modal.component';
import { EAvailabilityState } from '@okcargo-apps/shared-domain';
import { ECapabilities } from '@okcargo-apps/shared-domain-ionic';
import { FreightAvailableDetailStopComponent } from '../freight-available-detail-stop/freight-available-detail-stop.component';

@Component({
  selector: 'lib-freight-available-detail',
  templateUrl: './freight-available-detail.component.html',
  styleUrls: ['./freight-available-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    IonButton,
    CommonModule,
    IonList,
    IonItemGroup,
    IonItemDivider,
    TranslateModule,
    IonGrid,
    IonRow,
    IonCol,
    IonIcon,
    SpinnerLoadingComponent,
    SkeletonCardGenericComponent,
    FastpayCardCtaComponent,
    MessageUiIonicComponent,
    FreightAvailableDetailStopComponent,
  ],
})
export class FreightAvailableDetailComponent {
  public readonly itemVM = input.required<IFreightAvailableDetailedVM>();

  private readonly accountService = inject(AccountService);
  private readonly messagesUiService = inject(MessagesUiService);
  private readonly modalCtrl = inject(ModalController);
  // private readonly freightAvailableStoreService = inject(FreightAvailableStoreService);

  private readonly translate = inject(TranslateService);

  protected readonly freightAvailable = computed(() => this.itemVM().item);
  protected readonly isLoading = computed(() => this.itemVM().isLoading);
  protected readonly hasError = computed(() => this.itemVM().error);

  protected readonly isFullyAvailable = computed(() => {
    return this.freightAvailable()?.available === EAvailabilityState.FULL;
  });

  protected readonly isAvailableWithConditions = computed(() => {
    return this.freightAvailable()?.available === EAvailabilityState.PARTIAL;
  });

  protected readonly isNotAvailable = computed(() => {
    return this.freightAvailable()?.available === EAvailabilityState.NO;
  });

  protected readonly messages = computed(() => {
    const item = this.itemVM().item;
    return item ? this.messagesUiService.getMessagesForObjectId(item.id) : [];
  });

  protected readonly canViewPrices = computed(() => this.accountService.hasCapability(ECapabilities.VIEW_PRICES));

  protected async openTrafficContact(): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: HelpTrafficModalComponent,
      componentProps: {
        reference: this.freightAvailable()?.reference,
      },
      backdropDismiss: false,
    });
    modal.present();
  }
}
